// src/pages/SubmissionSuccess.jsx
import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { CheckCircle2, ArrowRight, Home } from 'lucide-react';

export default function SubmissionSuccess() {
  const navigate = useNavigate();
  const location = useLocation();
  const submissionId = location.state?.submissionId;

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 via-white to-blue-50 flex items-center justify-center p-4">
      <div className="max-w-2xl w-full animate-fade-in-up">
        {/* Success Icon */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-24 h-24 bg-gradient-to-br from-green-500 to-green-600 rounded-full mb-6 shadow-soft-xl animate-scale-in">
            <CheckCircle2 className="w-12 h-12 text-white" />
          </div>
          <h1 className="text-4xl font-display font-bold text-neutral-900 mb-4">
            Thank You! 🎉
          </h1>
          <p className="text-xl text-neutral-600">
            Your story has been successfully submitted!
          </p>
        </div>

        {/* Info Card */}
        <div className="card p-8 mb-6">
          <h2 className="text-2xl font-semibold text-neutral-900 mb-4">
            What Happens Next?
          </h2>
          
          <div className="space-y-4">
            <div className="flex gap-4">
              <div className="flex-shrink-0 w-8 h-8 bg-primary-100 text-primary-600 rounded-full flex items-center justify-center font-semibold">
                1
              </div>
              <div>
                <h3 className="font-semibold text-neutral-900 mb-1">Review & Confirmation</h3>
                <p className="text-sm text-neutral-600">
                  Our team will review your submission within 24 hours and send you a confirmation email.
                </p>
              </div>
            </div>

            <div className="flex gap-4">
              <div className="flex-shrink-0 w-8 h-8 bg-primary-100 text-primary-600 rounded-full flex items-center justify-center font-semibold">
                2
              </div>
              <div>
                <h3 className="font-semibold text-neutral-900 mb-1">Design & Layout</h3>
                <p className="text-sm text-neutral-600">
                  Your story will be beautifully designed for the Stories for Memories magazine (7-10 business days).
                </p>
              </div>
            </div>

            <div className="flex gap-4">
              <div className="flex-shrink-0 w-8 h-8 bg-primary-100 text-primary-600 rounded-full flex items-center justify-center font-semibold">
                3
              </div>
              <div>
                <h3 className="font-semibold text-neutral-900 mb-1">Draft Review</h3>
                <p className="text-sm text-neutral-600">
                  You'll receive a draft to review and approve before final publication.
                </p>
              </div>
            </div>

            <div className="flex gap-4">
              <div className="flex-shrink-0 w-8 h-8 bg-green-100 text-green-600 rounded-full flex items-center justify-center font-semibold">
                4
              </div>
              <div>
                <h3 className="font-semibold text-neutral-900 mb-1">Publication</h3>
                <p className="text-sm text-neutral-600">
                  Your story will be published in Stories for Memories magazine and shared with the world!
                </p>
              </div>
            </div>
          </div>

          {submissionId && (
            <div className="mt-6 p-4 bg-blue-50 rounded-xl">
              <p className="text-sm text-blue-900">
                <strong>Submission ID:</strong> {submissionId.slice(0, 8).toUpperCase()}
              </p>
              <p className="text-xs text-blue-700 mt-1">
                Keep this for your records
              </p>
            </div>
          )}
        </div>

        {/* Contact Card */}
        <div className="card p-6 bg-gradient-to-br from-primary-50 to-white mb-6">
          <h3 className="text-lg font-semibold text-neutral-900 mb-2">
            Have Questions?
          </h3>
          <p className="text-sm text-neutral-600 mb-3">
            Our team is here to help! Feel free to reach out anytime.
          </p>
          <a 
            href="mailto:contactstoriesformemories@gmail.com"
            className="text-sm text-primary-600 hover:text-primary-700 font-medium"
          >
            contactstoriesformemories@gmail.com →
          </a>
        </div>

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row gap-4">
          <button
            onClick={() => navigate('/dashboard')}
            className="flex-1 btn btn-primary"
          >
            <Home className="w-5 h-5" />
            Go to Dashboard
          </button>
          <button
            onClick={() => navigate('/submit')}
            className="flex-1 btn btn-outline"
          >
            Submit Another Story
            <ArrowRight className="w-5 h-5" />
          </button>
        </div>

        {/* Footer */}
        <div className="text-center mt-8 text-sm text-neutral-500">
          <p>Thank you for sharing your story with Stories for Memories</p>
        </div>
      </div>
    </div>
  );
}
